// src/integrations/providers/bitly/bitlyProviderColumns.js
// -----------------------------------------------------------------------------
// Bitly provider-owned table column definitions.
//
// Enterprise boundary:
//  • Bitly-specific labels, paths, pending text, and future metric definitions
//    live here.
//  • Generic table utilities do not know Bitly exists.
//  • This mirrors the backend provider pattern where Bitly API logic is isolated
//    under provider-specific modules.
//
// Metric strategy:
//  • Bitly click metrics are backend-owned cached provider metrics.
//  • The table renders values already written to formSubmissions.
//  • The frontend never calls Bitly directly.
//  • BigQuery receives append-only provider metric snapshots separately.
// -----------------------------------------------------------------------------

import {
  buildProviderMetricColumn,
  buildProviderOutputColumn,
  getProviderMetricColumnId,
  getProviderOutputColumnId,
} from '../../../utils/providerOutputColumnUtils';

export const BITLY_PROVIDER_ID = 'bitly';

export const BITLY_PROVIDER_GROUP = Object.freeze({
  id: BITLY_PROVIDER_ID,
  label: 'Bitly',
});

export const BITLY_COLUMN_AUDIENCE = Object.freeze({
  CUSTOMER: 'customer',
  DIAGNOSTIC: 'diagnostic',
  SYNC_METADATA: 'syncMetadata',
  EXPERIMENTAL: 'experimental',
});

export const BITLY_OUTPUT_KEYS = Object.freeze({
  SHORT_URL: 'shortUrl',
  STATUS: 'status',
  ERROR: 'error',
  LONG_URL: 'longUrl',
  RAW_VALUE: 'rawValue',
  LINK_ID: 'linkId',
  DOMAIN: 'domain',
  SOURCE: 'source',
  GENERATED_AT: 'generatedAt',
});

export const BITLY_METRIC_KEYS = Object.freeze({
  // V1 production metric:
  // Lifetime total clicks for this generated Bitly shortlink as of the
  // most recent backend sync.
  TOTAL_CLICKS: 'totalClicks',

  // V1 supporting metadata:
  // Last time the backend synced click metrics for this shortlink.
  REFRESHED_AT: 'refreshedAt',

  // Reserved for later customer-driven reporting needs.
  // Do not expose these by default until backend time-window sync exists.
  LAST_7_DAYS_CLICKS: 'last7DaysClicks',
  LAST_30_DAYS_CLICKS: 'last30DaysClicks',
});

export const BITLY_SHORT_URL_COLUMN_ID = getProviderOutputColumnId({
  provider: BITLY_PROVIDER_ID,
  outputKey: BITLY_OUTPUT_KEYS.SHORT_URL,
});

export const BITLY_STATUS_COLUMN_ID = getProviderOutputColumnId({
  provider: BITLY_PROVIDER_ID,
  outputKey: BITLY_OUTPUT_KEYS.STATUS,
});

export const BITLY_ERROR_COLUMN_ID = getProviderOutputColumnId({
  provider: BITLY_PROVIDER_ID,
  outputKey: BITLY_OUTPUT_KEYS.ERROR,
});

export const BITLY_TOTAL_CLICKS_COLUMN_ID = getProviderMetricColumnId({
  provider: BITLY_PROVIDER_ID,
  metricKey: BITLY_METRIC_KEYS.TOTAL_CLICKS,
});

// Alias used by the newer Bitly clicks sync implementation.
// Keep BITLY_TOTAL_CLICKS_COLUMN_ID for backward compatibility with the
// existing provider-column naming convention in this file.
export const BITLY_CLICKS_TOTAL_COLUMN_ID = BITLY_TOTAL_CLICKS_COLUMN_ID;

export const BITLY_LAST_7_DAYS_CLICKS_COLUMN_ID = getProviderMetricColumnId({
  provider: BITLY_PROVIDER_ID,
  metricKey: BITLY_METRIC_KEYS.LAST_7_DAYS_CLICKS,
});

export const BITLY_LAST_30_DAYS_CLICKS_COLUMN_ID = getProviderMetricColumnId({
  provider: BITLY_PROVIDER_ID,
  metricKey: BITLY_METRIC_KEYS.LAST_30_DAYS_CLICKS,
});

export const BITLY_REFRESHED_AT_COLUMN_ID = getProviderMetricColumnId({
  provider: BITLY_PROVIDER_ID,
  metricKey: BITLY_METRIC_KEYS.REFRESHED_AT,
});

function withBitlyGroup(extra = {}) {
  return {
    groupId: BITLY_PROVIDER_GROUP.id,
    groupLabel: BITLY_PROVIDER_GROUP.label,
    ...extra,
  };
}

export function buildBitlyShortUrlColumn({
  sourceUfi = null,
  visible = true,
  order = null,
  label = 'Bitly Short URL',
  extra = {},
} = {}) {
  return buildProviderOutputColumn({
    provider: BITLY_PROVIDER_ID,
    outputKey: BITLY_OUTPUT_KEYS.SHORT_URL,
    label,
    sourceUfi,
    visible,
    order,
    valuePaths: [
      'shortlinks.bitly.shortUrl',
      'bitlyShortUrl',
      'providerOutputs.bitly.shortUrl',
      'integrations.bitly.shortUrl',
    ],
    statusPaths: [
      'shortlinks.bitly.status',
      'bitlyShortlinkStatus',
      'providerOutputs.bitly.status',
      'integrations.bitly.status',
    ],
    errorPaths: [
      'shortlinks.bitly.error',
      'bitlyShortlinkError',
      'providerOutputs.bitly.error',
      'integrations.bitly.error',
    ],
    pendingPaths: [
      '_pendingShortlinks.bitly',
      '_pendingProviderOutputs.bitly',
    ],
    pendingText: 'Getting URL...',
    errorText: 'Short URL unavailable',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.CUSTOMER,
      customerFacing: true,
      copyEnabled: true,
      ...extra,
    }),
  });
}

export function buildBitlyStatusColumn({
  visible = false,
  order = null,
  label = 'Bitly Status',
  extra = {},
} = {}) {
  return buildProviderOutputColumn({
    provider: BITLY_PROVIDER_ID,
    outputKey: BITLY_OUTPUT_KEYS.STATUS,
    label,
    visible,
    order,
    valuePaths: [
      'shortlinks.bitly.status',
      'bitlyShortlinkStatus',
      'providerOutputs.bitly.status',
      'integrations.bitly.status',
    ],
    pendingPaths: [
      '_pendingShortlinks.bitly',
      '_pendingProviderOutputs.bitly',
    ],
    pendingText: 'queued',
    errorText: 'error',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.DIAGNOSTIC,
      customerFacing: false,
      diagnostics: true,
      ...extra,
    }),
  });
}

export function buildBitlyErrorColumn({
  visible = false,
  order = null,
  label = 'Bitly Error',
  extra = {},
} = {}) {
  return buildProviderOutputColumn({
    provider: BITLY_PROVIDER_ID,
    outputKey: BITLY_OUTPUT_KEYS.ERROR,
    label,
    visible,
    order,
    valuePaths: [
      'shortlinks.bitly.error',
      'bitlyShortlinkError',
      'providerOutputs.bitly.error',
      'integrations.bitly.error',
    ],
    statusPaths: [
      'shortlinks.bitly.status',
      'bitlyShortlinkStatus',
      'providerOutputs.bitly.status',
      'integrations.bitly.status',
    ],
    errorPaths: [
      'shortlinks.bitly.error',
      'bitlyShortlinkError',
      'providerOutputs.bitly.error',
      'integrations.bitly.error',
    ],
    errorText: 'Short URL unavailable',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.DIAGNOSTIC,
      customerFacing: false,
      diagnostics: true,
      ...extra,
    }),
  });
}

export function buildBitlyMetricColumn({
  metricKey,
  label,
  visible = false,
  order = null,
  valuePaths = [],
  statusPaths = [],
  errorPaths = [],
  pendingPaths = [],
  pendingText = 'Refreshing...',
  errorText = 'Unavailable',
  extra = {},
}) {
  return buildProviderMetricColumn({
    provider: BITLY_PROVIDER_ID,
    metricKey,
    label,
    visible,
    order,
    valuePaths,
    statusPaths,
    errorPaths,
    pendingPaths,
    pendingText,
    errorText,
    extra: withBitlyGroup(extra),
  });
}

export function buildBitlyTotalClicksColumn({
  visible = false,
  order = null,
  label = 'Bitly Clicks',
  extra = {},
} = {}) {
  return buildBitlyMetricColumn({
    metricKey: BITLY_METRIC_KEYS.TOTAL_CLICKS,
    label,
    visible,
    order,

    // Backend-owned cached metric paths.
    //
    // V1 backend writes:
    //   bitlyClicksTotal
    //   providerOutputs.bitly.metrics.clicksTotal
    //   shortlinks.bitly.clicks.total
    //
    // Legacy/future-compatible paths are kept after the canonical paths.
    valuePaths: [
      'bitlyClicksTotal',
      'providerOutputs.bitly.metrics.clicksTotal',
      'shortlinks.bitly.clicks.total',
      'shortlinks.bitly.metrics.totalClicks',
      'integrations.bitly.metrics.totalClicks',
    ],
    statusPaths: [
      'bitlyClicksStatus',
      'providerOutputs.bitly.metrics.clicksStatus',
      'shortlinks.bitly.clicks.status',
      'shortlinks.bitly.metrics.status',
      'shortlinks.bitly.status',
    ],
    errorPaths: [
      'bitlyClicksError',
      'providerOutputs.bitly.metrics.clicksError',
      'shortlinks.bitly.clicks.error',
      'shortlinks.bitly.metrics.error',
      'shortlinks.bitly.error',
      'bitlyShortlinkError',
    ],
    pendingPaths: [
      '_pendingProviderMetrics.bitly',
      '_pendingShortlinks.bitly',
      '_pendingProviderOutputs.bitly',
    ],
    pendingText: 'Waiting for link...',
    errorText: 'Clicks unavailable',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.CUSTOMER,
      customerFacing: true,
      sortable: true,
      copyEnabled: false,
      helperText:
        'Total Bitly clicks for this short link as of the last backend sync.',
      ...extra,
    }),
  });
}

// Alias used by the newer Bitly clicks sync implementation.
export const buildBitlyClicksTotalColumn = buildBitlyTotalClicksColumn;

export function buildBitlyMetricsRefreshedColumn({
  visible = false,
  order = null,
  label = 'Bitly Metrics Refreshed',
  extra = {},
} = {}) {
  return buildBitlyMetricColumn({
    metricKey: BITLY_METRIC_KEYS.REFRESHED_AT,
    label,
    visible,
    order,
    valuePaths: [
      'bitlyClicksSyncedAt',
      'providerOutputs.bitly.metrics.clicksSyncedAt',
      'shortlinks.bitly.clicks.syncedAt',
      'shortlinks.bitly.metrics.refreshedAt',
      'integrations.bitly.metrics.refreshedAt',
    ],
    statusPaths: [
      'bitlyClicksStatus',
      'providerOutputs.bitly.metrics.clicksStatus',
      'shortlinks.bitly.clicks.status',
      'shortlinks.bitly.metrics.status',
    ],
    errorPaths: [
      'bitlyClicksError',
      'providerOutputs.bitly.metrics.clicksError',
      'shortlinks.bitly.clicks.error',
      'shortlinks.bitly.metrics.error',
    ],
    pendingPaths: [
      '_pendingProviderMetrics.bitly',
    ],
    pendingText: 'Refreshing...',
    errorText: 'Unavailable',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.SYNC_METADATA,
      customerFacing: false,
      syncMetadata: true,
      sortable: true,
      copyEnabled: false,
      helperText:
        'The last time UTM Smart Manager synced Bitly click metrics.',
      ...extra,
    }),
  });
}

export function buildBitlyLast7DaysClicksColumn({
  visible = false,
  order = null,
  label = 'Bitly Clicks Last 7 Days',
  extra = {},
} = {}) {
  return buildBitlyMetricColumn({
    metricKey: BITLY_METRIC_KEYS.LAST_7_DAYS_CLICKS,
    label,
    visible,
    order,
    valuePaths: [
      'providerOutputs.bitly.metrics.last7DaysClicks',
      'shortlinks.bitly.metrics.last7DaysClicks',
      'integrations.bitly.metrics.last7DaysClicks',
    ],
    statusPaths: [
      'providerOutputs.bitly.metrics.status',
      'shortlinks.bitly.metrics.status',
    ],
    errorPaths: [
      'providerOutputs.bitly.metrics.error',
      'shortlinks.bitly.metrics.error',
    ],
    pendingPaths: [
      '_pendingProviderMetrics.bitly',
    ],
    pendingText: 'Refreshing...',
    errorText: 'Unavailable',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.EXPERIMENTAL,
      customerFacing: false,
      sortable: true,
      copyEnabled: false,
      experimental: true,
      ...extra,
    }),
  });
}

export function buildBitlyLast30DaysClicksColumn({
  visible = false,
  order = null,
  label = 'Bitly Clicks Last 30 Days',
  extra = {},
} = {}) {
  return buildBitlyMetricColumn({
    metricKey: BITLY_METRIC_KEYS.LAST_30_DAYS_CLICKS,
    label,
    visible,
    order,
    valuePaths: [
      'providerOutputs.bitly.metrics.last30DaysClicks',
      'shortlinks.bitly.metrics.last30DaysClicks',
      'integrations.bitly.metrics.last30DaysClicks',
    ],
    statusPaths: [
      'providerOutputs.bitly.metrics.status',
      'shortlinks.bitly.metrics.status',
    ],
    errorPaths: [
      'providerOutputs.bitly.metrics.error',
      'shortlinks.bitly.metrics.error',
    ],
    pendingPaths: [
      '_pendingProviderMetrics.bitly',
    ],
    pendingText: 'Refreshing...',
    errorText: 'Unavailable',
    extra: withBitlyGroup({
      audience: BITLY_COLUMN_AUDIENCE.EXPERIMENTAL,
      customerFacing: false,
      sortable: true,
      copyEnabled: false,
      experimental: true,
      ...extra,
    }),
  });
}

export function getBitlyCoreColumnDefinitions({
  sourceUfi = null,
  includeDiagnostics = false,
} = {}) {
  const customerFacingColumns = [
    buildBitlyShortUrlColumn({
      sourceUfi,
      visible: true,
    }),
  ].filter(Boolean);

  if (!includeDiagnostics) {
    return customerFacingColumns;
  }

  return [
    ...customerFacingColumns,
    buildBitlyStatusColumn({
      visible: false,
    }),
    buildBitlyErrorColumn({
      visible: false,
    }),
  ].filter(Boolean);
}

export function getBitlyMetricColumnDefinitions({
  includeSyncMetadata = false,
  includeExperimentalMetrics = false,
} = {}) {
  const customerFacingMetrics = [
    buildBitlyTotalClicksColumn({
      visible: false,
    }),
  ].filter(Boolean);

  const syncMetadataMetrics = includeSyncMetadata
    ? [
        buildBitlyMetricsRefreshedColumn({
          visible: false,
        }),
      ].filter(Boolean)
    : [];

  if (!includeExperimentalMetrics) {
    return [
      ...customerFacingMetrics,
      ...syncMetadataMetrics,
    ];
  }

  return [
    ...customerFacingMetrics,
    ...syncMetadataMetrics,
    buildBitlyLast7DaysClicksColumn({
      visible: false,
    }),
    buildBitlyLast30DaysClicksColumn({
      visible: false,
    }),
  ].filter(Boolean);
}

export function getBitlyProviderColumnDefinitions({
  sourceUfi = null,
  includeMetrics = false,
  includeDiagnostics = false,
  includeSyncMetadata = false,
  includeExperimentalMetrics = false,
} = {}) {
  const core = getBitlyCoreColumnDefinitions({
    sourceUfi,
    includeDiagnostics,
  });

  if (!includeMetrics) {
    return core;
  }

  return [
    ...core,
    ...getBitlyMetricColumnDefinitions({
      includeSyncMetadata,
      includeExperimentalMetrics,
    }),
  ];
}

export const bitlyProviderColumns = Object.freeze({
  provider: BITLY_PROVIDER_ID,
  groupId: BITLY_PROVIDER_GROUP.id,
  groupLabel: BITLY_PROVIDER_GROUP.label,
  defaultColumnId: BITLY_SHORT_URL_COLUMN_ID,
  getColumns: getBitlyProviderColumnDefinitions,
});
